import * as d3 from "d3";
import { BaseType } from "d3";
import {
    Dimensions,
    Placement,
    CalendarState,
    CalendarPreviewDrawerSettings,
    CalendarDrawerContent,
} from "burde";
import { drawRubberBand } from "svg/draw/draw-rubber-band";
import { drawCoverSpiral } from "svg/draw/draw-cover-spiral";
import {
    BleedSize,
    CalendarSizes,
    CalendarDimensions,
    LegacyFonts,
} from "svg/constants";
import { drawSvgDropShadow } from "svg/draw/draw-svg-dropshadow";
import { drawCoverPage } from "svg/draw/draw-cover";
import { drawBackPage } from "./draw-backside";
import * as FontFaceObserver from "fontfaceobserver";
import { D3Selection } from "./draw-calendar";
import { getNumberOfSpiralsFromCalendarSize } from "ui/helper";

const drawCoverPreview = (
    calendar: CalendarState,
    calendarPreviewDrawerSettings: CalendarPreviewDrawerSettings,
    calendarDrawerContent: CalendarDrawerContent,
    ref: HTMLDivElement | string = "body",
    onDrawCoverPreviewCompleted: () => void,
) => {
    const calendarSize =
        calendar.calendarSize !== "" ? calendar.calendarSize : "A5";
    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendarSize,
    );

    if (calendarDimensions == undefined) return;

    let container = d3.select(ref as BaseType);
    container.html("");

    const usedFontIds: Array<number> = [];
    const fontObservers: Array<FontFaceObserver> = [];

    if (calendar.coverTexts && calendar.coverTexts.length > 0) {
        for (const text of calendar.coverTexts) {
            if (text.fontSetName) {
                // Apply legacy fonts
                fontObservers.push(new FontFaceObserver(text.fontSetName));
                const legacyFont = LegacyFonts.find(
                    (x) => x.title === text.fontSetName,
                );
                if (legacyFont) {
                    const fontFace = `@font-face { font-family: "${legacyFont["font-family"]}"; src: url("${legacyFont.url}") format("${legacyFont.format}");`;
                    container.append("style").text(fontFace);
                    container.style("font-family", text.fontSetName);
                }
            } else if (text.fontId && !usedFontIds.includes(text.fontId)) {
                usedFontIds.push(text.fontId);
            }
        }
    }

    usedFontIds.forEach((id) => {
        const fontData = calendarDrawerContent.fonts
            ? calendarDrawerContent.fonts.fontResources.find((x) => x.id == id)
            : undefined;
        if (fontData !== undefined) {
            // If fontData has a format (eg. is a file and not a Google Font) we need to use a font-face inside a style tag. Otherwise we can just link the style.
            if (fontData.format) {
                const fontFace = `@font-face { font-family: "${fontData["font-family"]}"; src: url("${fontData.url}") format("${fontData.format}");`;
                container.append("style").text(fontFace);
            } else {
                container
                    .append("link")
                    .attr("id", "font-data")
                    .attr("href", fontData.url)
                    .attr("rel", "stylesheet");

                const existingFont = document.querySelector(
                    `link[data-font-title="${fontData.title}"]`,
                );

                if (!existingFont) {
                    const link = document.createElement("link");
                    link.dataset.fontTitle = fontData.title;
                    link.href = fontData.url;
                    link.rel = "stylesheet";
                    document.head.appendChild(link);
                }
            }

            fontObservers.push(new FontFaceObserver(fontData["font-family"]));
        }
    });

    //Fonts need to be loaded before we draw, if not there is a risk that texts
    //will be drawn out of bounds
    Promise.all(fontObservers).then(() => {
        const numberOfSpirals = getNumberOfSpiralsFromCalendarSize(
            calendar.calendarSize,
        );
        const drawPage = (reverse: boolean) => {
            svg.selectAll(".rubberBand").remove();
            svg.selectAll(".spiral").remove();

            const drawFront: boolean = reverse
                ? !calendarPreviewDrawerSettings.isFrontPage
                : calendarPreviewDrawerSettings.isFrontPage;

            if (drawFront) {
                drawCoverPage(
                    coverSvg,
                    CalendarSizes.Preview,
                    calendarDimensions,
                    calendarDrawerContent.coverImage,
                    calendar.coverColor,
                    calendarDrawerContent.customCoverImage,
                    calendar.coverTexts,
                    calendarDrawerContent.fonts,
                );

                if (calendarDrawerContent.spiral) {
                    drawCoverSpiral(
                        svg,
                        calendarDrawerContent.spiral,
                        false,
                        numberOfSpirals,
                        calendarDimensions,
                    );
                }
                if (calendarDrawerContent.rubberband) {
                    drawRubberBand(
                        svg,
                        calendarDrawerContent.rubberband,
                        calendarDimensions,
                    );
                }
            } else {
                drawBackPage(
                    coverSvg,
                    CalendarSizes.Preview,
                    calendarDimensions,
                    calendarDrawerContent.backCoverImage,
                    calendar.backCoverColor,
                    calendarDrawerContent.customBackImage,
                );

                if (calendarDrawerContent.spiral) {
                    drawCoverSpiral(
                        svg,
                        calendarDrawerContent.spiral,
                        true,
                        numberOfSpirals,
                        calendarDimensions,
                    );
                }
            }

            if (onDrawCoverPreviewCompleted) {
                onDrawCoverPreviewCompleted();
            }
        };

        container.html("");

        let svg = container.append("svg") as D3Selection;

        svg.attr("preserveAspectRatio", "xMidYMid meet")
            .attr(
                "viewBox",
                `0 0 ${
                    calendarDimensions.contentWidthWithoutBleeds +
                    calendarDimensions.presentationEmptySpaceWidth * 2
                } ${
                    calendarDimensions.contentHeightWithoutBleeds +
                    calendarDimensions.presentationEmptySpaceHeight * 2
                }`,
            )
            .classed("svg-content", true)
            .classed("coverPreview", true)
            .style("height", "100%")
            .style("width", "100%")
            .style("margin", "0 auto");

        let shadowDimensions: Dimensions = {
            Width: calendarDimensions.contentWidthWithoutBleeds,
            Height: calendarDimensions.contentHeightWithoutBleeds,
        };
        let shadowPlacement: Placement = {
            X: calendarDimensions.presentationEmptySpaceWidth,
            Y: calendarDimensions.presentationEmptySpaceHeight,
        };

        drawSvgDropShadow(svg, shadowDimensions, shadowPlacement);

        // Adds a plastic cover
        if (calendarPreviewDrawerSettings.withPlasticCover) {
            svg.append("svg:image")
                .attr("x", 0)
                .attr("y", 0)
                .attr(
                    "width",
                    calendarDimensions.contentWidthWithoutBleeds + 40,
                )
                .attr(
                    "height",
                    calendarDimensions.contentHeightWithoutBleeds + 40,
                )
                .attr("xlink:href", "/images/plastic-overlay.png")
                .attr(
                    "transform",
                    `translate(${
                        calendarDimensions.presentationEmptySpaceWidth - 20
                    } ${calendarDimensions.presentationEmptySpaceHeight - 20})`,
                )
                .attr("preserveAspectRatio", "none")
                .style("opacity", 0.5);
        }
        // -- END Plastic cover

        const coverSvg = svg
            .append("g")
            .attr(
                "transform",
                `translate(${calendarDimensions.presentationEmptySpaceWidth},${calendarDimensions.presentationEmptySpaceHeight})`,
            )
            .attr("id", "coverSvg")
            .append("svg")
            .attr("width", calendarDimensions.contentWidthWithoutBleeds)
            .attr("height", calendarDimensions.contentHeightWithoutBleeds)
            .attr(
                "viewBox",
                `${BleedSize} ${BleedSize} ${calendarDimensions.contentWidthWithoutBleeds} ${calendarDimensions.contentHeightWithoutBleeds}`,
            )
            .classed("leftPageSvg", true) as D3Selection;

        var defs = svg.select("defs");
        if (defs.empty()) {
            defs = svg.append("defs");
        }

        drawPage(calendarPreviewDrawerSettings.withAnimation);

        if (
            calendarPreviewDrawerSettings.showCalendarName &&
            !!calendarDrawerContent.calendarName
        ) {
            svg.append("text")
                .attr(
                    "x",
                    (calendarDimensions.contentWidthWithoutBleeds +
                        calendarDimensions.presentationEmptySpaceWidth * 2) /
                        2,
                )
                .attr("y", calendarDimensions.presentationEmptySpaceHeight - 50)
                .attr("font-size", 50 + "pt")
                .attr("text-anchor", "middle")
                .attr("font-family", "Quicksand, Helvetica, Arial, sans-serif")
                .attr("fill", "#57585A")
                .attr("font-weight", 600)
                .text(calendarDrawerContent.calendarName);
        }
    });
};

export { drawCoverPreview };
